<script setup lang="ts">
import { QaEquipmentCatalogDto, QaEquipmentRtSystemDto } from '@/api/generator';
import RTSystemPairRowParticleStream from './RTSystemPairRowParticleStream.vue';
import RTSystemTile from './RTSystemTile.vue';

/**
 * Two QA equipment tiles shown as one connected pair: the detector interface
 * (slot 0) and its array detector (slot 1), each taking half the width and
 * joined by a bridge spanning the gap between them. The tiles are rendered
 * prominent (larger image, minimum height) since the pair is what the view is
 * about. The caller is responsible for passing exactly the pair, in that order
 * (see orderSystemsForBeamAnalysis and isBeamAnalysisPair in QaSectionUtil).
 * Takes the full RT system DTOs (task setup) as well as the catalog DTOs a job
 * occurrence delivers. The bridge slot puts a badge (e.g. the zero measurement
 * button) on the bridge, centered on the gap.
 */
defineProps<{
  systems: (QaEquipmentRtSystemDto | QaEquipmentCatalogDto)[];
  dontShowDeviceState?: boolean;
  /**
   * Shows a stream of particles flowing out from behind the bridge badge into the interface
   * while a zero measurement settles (see RTSystemPairRowParticleStream for
   * how it stops).
   */
  settling?: boolean;
}>();
</script>

<template>
  <div class="tile-pair-row">
    <div v-for="(system, index) in systems" :key="system.id" class="tile-cell">
      <RTSystemTile :item="system" :dontShowDeviceState="dontShowDeviceState" prominent />
      <template v-if="index < systems.length - 1">
        <div class="tile-bridge" aria-hidden="true" />
        <!-- badge centered on the gap between the tiles, on the bridge -->
        <div v-if="$slots.bridge" class="tile-bridge-badge">
          <slot name="bridge" />
        </div>
        <RTSystemPairRowParticleStream :active="settling" />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use './pairRowGeometry' as geometry;

$tile-column-gap: geometry.$tile-column-gap;
$tile-bridge-overlap: 4px;
// Floor for the pair tiles: above an ordinary grid tile (136px on desktop,
// less on narrow screens), so the pair never reads as smaller than the rest.
$tile-min-height: 152px;
// Rounder than an ordinary tile (4px): the combined silhouette reads as one card.
$tile-radius: 16px;
// The bridge starts 8px below the end of the tiles' corner curve. That 8px is
// filled by a concave fillet on each side, so the convex tile corner flows
// into the bridge edge as one S-curve instead of meeting it at a right angle.
$bridge-fillet: 8px;
$bridge-inset: $tile-radius + $bridge-fillet;

// Row holding only the interface/array pair, each tile taking half the width.
.tile-pair-row {
  display: flex;
  gap: $tile-column-gap;

  // One shadow around the combined silhouette of the two tiles and the
  // bridge: the tiles' own box-shadows are turned off and replaced by a
  // drop-shadow filter on the row, which follows the union outline of the
  // opaque shapes (approximating the tiles' elevation-2).
  filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.14)) drop-shadow(0px 1px 4px rgba(0, 0, 0, 0.1));

  // Stacking inside the row, bottom to top: card backgrounds (z auto), bridge
  // (2), particle stream (3, see RTSystemPairRowParticleStream), card content
  // (4), badge (5). The card gives up its own stacking context (Vuetify's
  // z-index: 0) so the particles, which live outside the cards, can pass
  // between a card's background and its content, and the badge sits on top.
  :deep(.v-card.system-tile) {
    height: 100%; // equal-height tiles, so the bridge centers on both cards
    min-height: $tile-min-height;
    border-radius: $tile-radius;
    box-shadow: none !important;
    z-index: auto;

    .v-card-item {
      position: relative;
      z-index: 4;
    }
  }
}

// Anchor for the bridge sitting in the gap after a tile.
.tile-cell {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
}

// Bridge between the detector interface and its array detector: a
// surface-colored strip spanning the column gap, overlapping each card edge
// by a few pixels and running almost the full tile height. It carries no
// shadow of its own; the row's drop-shadow filter wraps both tiles and the
// bridge in one continuous outline.
.tile-bridge {
  position: absolute;
  top: $bridge-inset;
  right: -($tile-column-gap + $tile-bridge-overlap);
  bottom: $bridge-inset;
  z-index: 2;
  width: $tile-column-gap + 2 * $tile-bridge-overlap;
  background: rgb(var(--v-theme-surface));

  // Fillets: a strip across the gap just outside each bridge edge, painted in
  // surface color except for a transparent quarter circle at each outer
  // corner, so the edge curves into the tile. The half-pixel stops keep the
  // curve anti-aliased. The row's drop-shadow follows the resulting alpha outline.
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: $tile-bridge-overlap;
    width: $tile-column-gap;
    height: $bridge-fillet;
    background-color: transparent;
    background-repeat: no-repeat;
    background-size: $bridge-fillet $bridge-fillet;
  }

  &::before {
    bottom: 100%;
    background-image:
      radial-gradient(
        circle at top right,
        transparent calc($bridge-fillet - 0.5px),
        rgb(var(--v-theme-surface)) calc($bridge-fillet + 0.5px)
      ),
      radial-gradient(
        circle at top left,
        transparent calc($bridge-fillet - 0.5px),
        rgb(var(--v-theme-surface)) calc($bridge-fillet + 0.5px)
      );
    background-position:
      left bottom,
      right bottom;
  }

  &::after {
    top: 100%;
    background-image:
      radial-gradient(
        circle at bottom right,
        transparent calc($bridge-fillet - 0.5px),
        rgb(var(--v-theme-surface)) calc($bridge-fillet + 0.5px)
      ),
      radial-gradient(
        circle at bottom left,
        transparent calc($bridge-fillet - 0.5px),
        rgb(var(--v-theme-surface)) calc($bridge-fillet + 0.5px)
      );
    background-position:
      left top,
      right top;
  }
}

// Badge (e.g. the zero measurement button): centered on the gap, overlapping
// both tile edges, above the cards' content. It stays put; whatever it shows
// while a run is under way (the button grows) happens in place.
.tile-bridge-badge {
  position: absolute;
  top: 50%;
  right: -(math.div($tile-column-gap, 2));
  z-index: 5;
  transform: translate(50%, -50%);
}
</style>
